import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Package, ShoppingBag, KeyRound, Store, Settings, FileText, Download, X,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const TABS = [
  { id: "products", label: "My Products", icon: Package },
  { id: "orders", label: "My Orders", icon: ShoppingBag },
  { id: "licenses", label: "My Licenses", icon: KeyRound },
  { id: "settings", label: "Account", icon: Settings },
];

const AccessModal = ({ purchase, mode = "product", onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/me/purchases/${purchase.id}/access`).then((r) => setData(r.data)).catch(() => {});
  }, [purchase]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0B0B0D] border border-white/10 rounded-2xl p-8 max-w-lg w-full"
        data-testid="access-modal"
      >
        <div className="flex items-start justify-between mb-6">
          <h3 className="text-xl font-bold text-white">{purchase.product_name}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {!data ? (
          <div className="py-8 flex justify-center">
            <div className="w-8 h-8 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-5">
            {mode === "product" ? (
  <>
    <p className="text-gray-400 text-sm">
      {data.delivery_info}
    </p>

    {data.delivery_pdf_url ? (
      <a
        href={data.delivery_pdf_url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="access-download"
      >
        <Button className="w-full bg-gradient-to-r from-purple-600 to-purple-500 text-white py-4 rounded-xl">
          <Download className="w-4 h-4 mr-2" />
          Open Product Access
        </Button>
      </a>
    ) : (
      <div className="p-4 bg-white/5 border border-white/10 rounded-xl text-sm text-gray-400">
        <FileText className="w-4 h-4 text-purple-400 mb-2" />
        Your product access is being prepared. Please check back shortly or contact support.
      </div>
    )}
  </>
) : (
  <>
    <span className="inline-block px-3 py-1 bg-purple-500/15 text-purple-300 text-xs font-semibold rounded-full capitalize">
      {data.license_type} License
    </span>

    {data.license_pdf_url ? (
      <a
        href={data.license_pdf_url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="license-download"
      >
        <Button className="w-full bg-gradient-to-r from-purple-600 to-purple-500 text-white py-4 rounded-xl">
          <Download className="w-4 h-4 mr-2" />
          Open License PDF
        </Button>
      </a>
    ) : (
      <div className="p-4 bg-white/5 border border-white/10 rounded-xl text-sm text-gray-400">
        <FileText className="w-4 h-4 text-purple-400 mb-2" />
        Your license document is being prepared. Please check back shortly or contact support.
      </div>
    )}

    {data.license_rights?.length > 0 && (
      <div>
        <p className="text-sm font-semibold text-white mb-2">
          Your license rights
        </p>

        <ul className="space-y-1">
          {data.license_rights.map((r, i) => (
            <li key={i} className="text-sm text-gray-400">
              • {r}
            </li>
          ))}
        </ul>
      </div>
    )}
  </>
)}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("products");
  const [purchases, setPurchases] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedMode, setSelectedMode] = useState("product");

  useEffect(() => {
    api.get("/me/purchases").then((r) => setPurchases(r.data)).catch(() => {});
    api.get("/me/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, []);

  const statusColor = (s) =>
    ({ PAID: "text-green-400", COMPLETED: "text-green-400", PARTIALLY_REFUNDED: "text-blue-400", PENDING: "text-yellow-400", CREATED: "text-yellow-400", AUTHORIZED: "text-purple-400", FAILED: "text-red-400", CANCELLED: "text-gray-400", REFUNDED: "text-blue-400", CHARGEBACK: "text-red-400" }[String(s || "").toUpperCase()] || "text-gray-400");

  return (
    <div className="min-h-screen bg-[#050506] pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-white">My OrvDigital</h1>
          <p className="text-gray-400 mt-1">Welcome back, {user?.name}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="space-y-1 sticky top-28">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`dash-tab-${t.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    tab === t.id ? "bg-purple-600 text-white" : "text-gray-400 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <t.icon className="w-4 h-4" /> {t.label}
                </button>
              ))}
              <Link
                to="/sell"
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-400 hover:bg-white/5 hover:text-white transition-all"
              >
                <Store className="w-4 h-4" /> Sell with OrvDigital
              </Link>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            {tab === "products" && (
              <div>
                {purchases.length === 0 ? (
                  <div className="text-center py-20 border border-white/10 rounded-2xl bg-white/5">
                    <Package className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400 mb-4">You haven't purchased any products yet.</p>
                    <Link to="/products"><Button className="bg-purple-600 text-white">Explore Products</Button></Link>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-6">
                    {purchases.map((p) => (
                      <div key={p.id} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid="purchased-product">
                        {p.cover_image && <img src={p.cover_image} alt="" className="w-full h-40 object-cover" />}
                        <div className="p-5">
                          <h3 className="font-semibold text-white mb-1">{p.product_name}</h3>
                          <span className="inline-block px-2 py-0.5 bg-purple-500/15 text-purple-300 text-xs rounded-full capitalize mb-4">
                            {p.license_type} License
                          </span>
                          <Button
  onClick={() => {
    setSelectedMode("product");
    setSelected(p);
  }}
  className="w-full bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-xl"
  data-testid="access-product-btn"
>
                            Access Product
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === "orders" && (
              <div className="space-y-3">
                {orders.length === 0 ? (
                  <p className="text-gray-400 py-20 text-center">No orders yet.</p>
                ) : (
                  orders.map((o) => (
                    <div key={o.id} className="p-5 bg-white/5 border border-white/10 rounded-xl">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="text-white font-medium">{o.items?.[0]?.product_name}</p>
                          <p className="text-xs text-gray-500">{o.order_number} · {new Date(o.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-bold">₹{o.amount}</p>
                          <p className={`text-xs ${statusColor(o.status)}`}>{String(o.status || "").replaceAll("_", " ")}</p>
                        </div>
                      </div>
                      {o.timeline?.length > 0 && (
                        <details className="mt-4 border-t border-white/5 pt-3">
                          <summary className="cursor-pointer text-xs text-purple-400">Payment timeline</summary>
                          <div className="mt-3 space-y-3">
                            {[...o.timeline].reverse().map((entry, index) => (
                              <div key={`${entry.event_id || entry.timestamp}-${index}`} className="flex gap-3 text-xs">
                                <span className={`mt-1 h-2 w-2 rounded-full bg-current ${statusColor(entry.status)}`} />
                                <div>
                                  <p className="text-gray-300">{String(entry.status || "").replaceAll("_", " ")}</p>
                                  <p className="text-gray-500">{entry.detail || entry.event_type || entry.source}</p>
                                  <p className="text-gray-600">{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ""}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {tab === "licenses" && (
              <div className="space-y-3">
                {purchases.length === 0 ? (
                  <p className="text-gray-400 py-20 text-center">No licenses yet.</p>
                ) : (
                  purchases.map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-5 bg-white/5 border border-white/10 rounded-xl">
                      <div>
                        <p className="text-white font-medium">{p.product_name}</p>
                        <p className="text-xs text-gray-500 capitalize">{p.license_type} License</p>
                      </div>
                      <Button
  onClick={() => {
    setSelectedMode("license");
    setSelected(p);
  }} variant="outline" className="border-white/20 text-white bg-transparent">
                        View License
                      </Button>
                    </div>
                  ))
                )}
              </div>
            )}

            {tab === "settings" && (
              <div className="p-6 bg-white/5 border border-white/10 rounded-2xl space-y-4 max-w-md">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Name</p>
                  <p className="text-white">{user?.name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Email</p>
                  <p className="text-white">{user?.email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Role</p>
                  <p className="text-white capitalize">{user?.role}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {selected && (
  <AccessModal
    purchase={selected}
    mode={selectedMode}
    onClose={() => setSelected(null)}
  />
)}
    </div>
  );
}
